import { useEffect, useRef, useState } from "react";
import { Bluetooth, BluetoothConnected, BluetoothSearching, Scale, Power, RotateCcw, CheckCircle2, Activity } from "lucide-react";

// Smoothly eases a displayed number toward the live target (count-up feel).
function useEased(target) {
  const [val, setVal] = useState(0);
  const ref = useRef(0);
  const raf = useRef(null);
  useEffect(() => {
    const tick = () => {
      const cur = ref.current;
      const next = cur + (target - cur) * 0.25;
      if (Math.abs(target - next) < 0.05) { ref.current = target; setVal(target); return; }
      ref.current = next; setVal(next);
      raf.current = requestAnimationFrame(tick);
    };
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target]);
  return val;
}

/**
 * Live Bluetooth scale readout + controls with a weighing animation.
 * `variant`: "dark" (kiosk) | "light" (in-app). `scale` is the useBleScale() value.
 */
export default function ScaleWidget({ scale, variant = "dark", testid = "scale-widget" }) {
  const SCALE_ENGINE_VERSION = "v13";
  const btReady = typeof navigator !== "undefined" && !!navigator.bluetooth;
  const hasRequest = btReady && typeof navigator.bluetooth.requestDevice === "function";
  const { supported, status, deviceName, protocol, error, weight, raw, stable, connected, connecting, connect, reconnect, disconnect, zero, hasHardwareTare, knownName, debug = [], frameCount = 0, dropCount = 0, writeStatus = "", pathInfo = "" } = scale;
  const eased = useEased(connected ? weight : 0);
  const dark = variant === "dark";
  const [showCal, setShowCal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [btAvail, setBtAvail] = useState("checking…");
  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        if (navigator.bluetooth?.getAvailability) {
          const a = await navigator.bluetooth.getAvailability();
          if (ok) setBtAvail(a ? "ON" : "OFF / blocked");
        } else if (ok) setBtAvail("unknown");
      } catch { if (ok) setBtAvail("error"); }
    })();
    return () => { ok = false; };
  }, []);
  const standalone = typeof window !== "undefined" && (
    window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
  );
  const secure = typeof window !== "undefined" ? window.isSecureContext : true;

  const isIOS = typeof navigator !== "undefined" && (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
  const inFrame = typeof window !== "undefined" && window.self !== window.top;
  const copyLink = () => {
    try {
      navigator.clipboard?.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* */ }
  };

  const panel = dark
    ? "bg-white/5 border-white/10 text-white"
    : "bg-white border-slate-200 text-slate-900";
  const sub = dark ? "text-white/50" : "text-slate-500";
  const active = !stable && connected;

  if (!supported && status === "idle") {
    if (isIOS) {
      return (
        <div className={`rounded-2xl border px-4 py-3 ${panel}`} data-testid={`${testid}-ios-bluefy`}>
          <div className="flex items-start gap-2">
            <Bluetooth className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-fuchsia-300" : "text-fuchsia-500"}`} />
            <div className="min-w-0">
              <p className="text-xs font-semibold font-['Manrope']">Connect your Acaia on iPad</p>
              <p className={`text-xs font-['Manrope'] mt-0.5 ${sub}`}>
                Apple blocks Bluetooth in Safari. Install the free <strong>Bluefy</strong> browser, then open this page in Bluefy — the Connect button appears automatically.
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <a href="https://apps.apple.com/us/app/bluefy-web-ble-browser/id1492822055" target="_blank" rel="noreferrer"
                  data-testid={`${testid}-get-bluefy`}
                  className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold font-['Manrope'] ${
                    dark ? "bg-white text-[#120a24] hover:bg-white/90" : "bg-fuchsia-600 text-white hover:bg-fuchsia-700"}`}>
                  <Bluetooth className="w-3.5 h-3.5" /> Get Bluefy
                </a>
                <button onClick={copyLink} data-testid={`${testid}-copy-link`}
                  className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold font-['Manrope'] ${
                    dark ? "bg-white/10 text-white hover:bg-white/20" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                  {copied ? <><CheckCircle2 className="w-3.5 h-3.5" /> Copied!</> : "Copy page link"}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={`rounded-2xl border px-4 py-3 ${panel}`} data-testid={`${testid}-unsupported`}>
        <div className="flex items-center gap-2">
          <Bluetooth className={`w-4 h-4 ${sub}`} />
          <span className={`text-xs font-['Manrope'] ${sub}`}>Bluetooth scales (e.g. Acaia Pearl) need Chrome or Edge on a laptop/desktop. Weigh manually here.</span>
        </div>
      </div>
    );
  }

  if (!connected) {
    return (
      <div className={`rounded-2xl border px-4 py-3 ${panel}`} data-testid={`${testid}-idle`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Scale className={`w-4 h-4 shrink-0 ${dark ? "text-fuchsia-300" : "text-fuchsia-500"}`} />
            <div className="min-w-0">
              <span className="text-sm font-semibold font-['Manrope'] block leading-tight">Smart scale</span>
              {knownName && !connecting && (
                <span className={`text-[11px] font-['Manrope'] truncate block ${sub}`} data-testid={`${testid}-known`}>
                  Paired: {knownName}
                </span>
              )}
            </div>
          </div>
          {knownName ? (
            <button onClick={reconnect} disabled={connecting} data-testid={`${testid}-reconnect`}
              className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-semibold font-['Manrope'] transition-colors disabled:opacity-50 ${
                dark ? "bg-white text-[#120a24] hover:bg-white/90" : "bg-fuchsia-600 text-white hover:bg-fuchsia-700"}`}>
              {connecting ? <><BluetoothSearching className="w-4 h-4 animate-pulse" />Reconnecting…</> : <><BluetoothConnected className="w-4 h-4" />Reconnect</>}
            </button>
          ) : (
            <button onClick={connect} disabled={connecting} data-testid={`${testid}-connect`}
              className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-semibold font-['Manrope'] transition-colors disabled:opacity-50 ${
                dark ? "bg-white text-[#120a24] hover:bg-white/90" : "bg-fuchsia-600 text-white hover:bg-fuchsia-700"}`}>
              {connecting ? <><BluetoothSearching className="w-4 h-4 animate-pulse" />Searching…</> : <><Bluetooth className="w-4 h-4" />Connect scale</>}
            </button>
          )}
        </div>
        {knownName && !connecting && (
          <button onClick={connect} data-testid={`${testid}-choose-another`}
            className={`text-[11px] font-['Manrope'] mt-2 underline-offset-2 hover:underline ${sub}`}>
            Choose another scale
          </button>
        )}
        {inFrame && (
          <p className={`text-[11px] mt-2 font-['Manrope'] ${dark ? "text-amber-300" : "text-amber-600"}`} data-testid={`${testid}-iframe-tip`}>
            Tip: open the Color Bar in its own browser tab — Bluetooth is blocked inside this embedded preview.
          </p>
        )}
        {standalone && (
          <p className={`text-[11px] mt-2 font-['Manrope'] font-semibold ${dark ? "text-amber-300" : "text-amber-600"}`} data-testid={`${testid}-standalone-tip`}>
            ⚠ This looks like a Home-Screen web app. Bluetooth is disabled there — open the Color Bar from a normal tab inside the Bluefy app (type/paste the link in Bluefy's address bar), not a home-screen icon.
          </p>
        )}
        {error && <p className={`text-xs mt-2 font-['Manrope'] ${dark ? "text-rose-300" : "text-rose-600"}`} data-testid={`${testid}-error`}>{error}</p>}
        <p className={`text-[10px] mt-2 font-['Manrope'] ${sub}`} data-testid={`${testid}-diag`}>
          Scale engine {SCALE_ENGINE_VERSION} · Bluetooth API: {btReady ? (hasRequest ? "available" : "partial") : "not available in this browser"}
        </p>
        <p className={`text-[10px] mt-0.5 font-['Manrope'] ${sub}`} data-testid={`${testid}-env-diag`}>
          radio: <b>{btAvail}</b> · launched: <b>{standalone ? "home-screen app ⚠" : "browser tab ✓"}</b> · {secure ? "secure ✓" : "not secure ⚠"} · {inFrame ? "embedded ⚠" : "top window ✓"}
        </p>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-2xl border px-4 py-4 ${panel}`} data-testid={`${testid}-connected`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <BluetoothConnected className={`w-4 h-4 ${dark ? "text-sky-300" : "text-sky-500"}`} />
          <span className="text-xs font-semibold font-['Manrope'] truncate">{deviceName}</span>
          {protocol && (
            <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
              protocol === "acaia" ? (dark ? "bg-fuchsia-400/20 text-fuchsia-200" : "bg-fuchsia-100 text-fuchsia-700")
              : (dark ? "bg-white/10 text-white/60" : "bg-slate-100 text-slate-500")}`} data-testid={`${testid}-protocol`}>
              {protocol}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={zero} data-testid={`${testid}-zero`}
            className={`inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-semibold font-['Manrope'] ${
              dark ? "bg-amber-400/90 text-[#120a24] hover:bg-amber-400" : "bg-amber-500 text-white hover:bg-amber-600"}`}>
            <RotateCcw className="w-3.5 h-3.5" /> {hasHardwareTare ? "Tare scale" : "Zero"}
          </button>
          <button onClick={disconnect} data-testid={`${testid}-disconnect`}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${dark ? "bg-white/10 text-white/60 hover:text-white" : "bg-slate-100 text-slate-500 hover:text-slate-800"}`}>
            <Power className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Live link diagnostics — tells us instantly if data is flowing */}
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-['Manrope'] mb-2 ${sub}`} data-testid={`${testid}-live-diag`}>
        <span>engine {SCALE_ENGINE_VERSION}</span>
        <span>· frames: <b className={frameCount > 0 ? (dark ? "text-emerald-300" : "text-emerald-600") : (dark ? "text-rose-300" : "text-rose-600")} data-testid={`${testid}-framecount`}>{frameCount}</b></span>
        <span>· writes: <b className={writeStatus.startsWith("ERR") ? (dark ? "text-rose-300" : "text-rose-600") : (dark ? "text-emerald-300" : "text-emerald-600")} data-testid={`${testid}-writestatus`}>{writeStatus || "—"}</b></span>
        <span>· drops: <b data-testid={`${testid}-dropcount`}>{dropCount}</b></span>
        {pathInfo && <span>· path: <b data-testid={`${testid}-pathinfo`}>{pathInfo}</b></span>}
      </div>

      {/* Animated weighing dial */}
      <div className="flex items-center justify-center py-2">
        <div className="relative w-40 h-40 flex items-center justify-center" data-testid={`${testid}-dial`}>
          {/* pulsing rings while weight is changing */}
          <span className={`absolute inset-0 rounded-full border ${dark ? "border-fuchsia-400/30" : "border-fuchsia-300"} ${active ? "scale-weigh-ping" : "opacity-40"}`} />
          <span className={`absolute inset-3 rounded-full border ${dark ? "border-sky-400/30" : "border-sky-300"} ${active ? "scale-weigh-ping scale-weigh-delay" : "opacity-30"}`} />
          <div className={`absolute inset-6 rounded-full ${dark ? "bg-white/5" : "bg-slate-50"} ${active ? "scale-weigh-glow" : ""} flex flex-col items-center justify-center`}>
            <span className="text-4xl font-semibold font-['Outfit'] tabular-nums leading-none" data-testid={`${testid}-reading`}>
              {eased.toFixed(1)}
            </span>
            <span className={`text-xs font-['Manrope'] mt-1 ${sub}`}>grams</span>
            <span className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold font-['Manrope'] uppercase tracking-wider ${
              active ? (dark ? "text-fuchsia-300" : "text-fuchsia-500") : (dark ? "text-emerald-300" : "text-emerald-600")}`}>
              {active ? <><Scale className="w-3 h-3 scale-weigh-bob" /> weighing</> : <><CheckCircle2 className="w-3 h-3" /> stable</>}
            </span>
          </div>
        </div>
      </div>

      {/* Calibration readout — confirm the number matches a known weight */}
      <div className={`mt-1 border-t pt-2 ${dark ? "border-white/10" : "border-slate-100"}`}>
        <button onClick={() => setShowCal((v) => !v)} data-testid={`${testid}-calibration-toggle`}
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold font-['Manrope'] ${sub} hover:opacity-80`}>
          <Activity className="w-3 h-3" /> {showCal ? "Hide" : "Show"} calibration
        </button>
        {showCal && (
          <div className="mt-2 space-y-1.5" data-testid={`${testid}-calibration`}>
            <div className={`flex items-center justify-between text-[11px] font-['Manrope'] ${sub}`}>
              <span>Raw from scale</span>
              <span className="tabular-nums font-semibold" data-testid={`${testid}-raw`}>{raw?.toFixed?.(2)} g</span>
            </div>
            <div className={`flex items-center justify-between text-[11px] font-['Manrope'] ${sub}`}>
              <span>Shown (after tare)</span>
              <span className="tabular-nums font-semibold">{weight?.toFixed?.(1)} g</span>
            </div>
            <p className={`text-[10px] font-['Manrope'] ${sub}`}>
              Put a known weight on the scale. If “Raw” doesn’t match, share these lines and I’ll correct the reader.
            </p>
            <div className={`rounded-lg p-2 font-mono text-[10px] leading-relaxed overflow-x-auto ${dark ? "bg-black/30 text-sky-200" : "bg-slate-900 text-sky-200"}`}>
              {debug.length === 0 ? (
                <span className="opacity-60">waiting for data…</span>
              ) : debug.map((d, i) => (
                <div key={d.t + "-" + i} className="whitespace-nowrap">
                  <span className="opacity-60">[{d.len}B]</span> {d.hex} <span className="opacity-60">→</span> {d.val == null ? "—" : d.val.toFixed(1)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
